import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import { AuthContext } from '../context/AuthContext';
import { cuplAPI } from '../context/api';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import axios from '../api/axiosConfig';
import DashboardTramitesModal from '../components/DashboardTramitesModal';
import DashboardCuplsModal from '../components/DashboardCuplsModal';
import './Dashboard.css';

// Gráficas de barras horizontales con muchas categorías (p. ej. puntos de venta):
// la altura crece con la cantidad de filas y el contenedor hace scroll, para que
// las etiquetas no se encimen. Los nombres largos se recortan; el tooltip muestra el completo.
const BAR_ROW_HEIGHT = 30;
const barChartHeight = (data) => Math.max(260, (data?.length || 0) * BAR_ROW_HEIGHT + 40);
const truncateLabel = (value, max = 24) => {
  const s = String(value ?? '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Estados para filtros
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeFilter, setActiveFilter] = useState('day');

  // Estados para datos
  const [stats, setStats] = useState(null);
  const [tramiteStats, setTramiteStats] = useState([]);
  const [balance, setBalance] = useState(0);
  const [recentCUPLs, setRecentCUPLs] = useState([]);
  const [chartData, setChartData] = useState({ paidCupls: [], income: [] });
  const [pendingCount, setPendingCount] = useState(0);
  const [totalTramites, setTotalTramites] = useState(0);
  const [esperandoPreasignacion, setEsperandoPreasignacion] = useState(0);
  const [esperandoSoat, setEsperandoSoat] = useState(0);
  const [esperandoCupl, setEsperandoCupl] = useState(0);
  const [tienenSoat, setTienenSoat] = useState(0);
  const [listosParaFinalizar, setListosParaFinalizar] = useState(0);
  const [otrosTramites, setOtrosTramites] = useState(0);
  const [intentosEjecucion, setIntentosEjecucion] = useState(0);
  const [cambiosTipoCount, setCambiosTipoCount] = useState(0);
  // "Cambios Trámites" es exclusivo de superadmin
  const canSeeCambiosTramites = user?.role === 'superadmin' || user?.profile_name === 'superadmin';
  const [pendingNovelties, setPendingNovelties] = useState(0);
  const [pendingRangeRequests, setPendingRangeRequests] = useState(0);
  const [pendingDeletionsCount, setPendingDeletionsCount] = useState(0);
  const [pendingCambioPlacaCount, setPendingCambioPlacaCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilterType, setModalFilterType] = useState('');
  const [modalFilterTitle, setModalFilterTitle] = useState('');
  const [hasAssignedPlates, setHasAssignedPlates] = useState(true);
  const [transitoAlerts, setTransitoAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entityMovement, setEntityMovement] = useState({
    transitos: [],
    concesionarios: [],
    puntosDeVenta: [],
    zonas: [],
    zonasDetalle: [],
    asesores: [],
    marcasDetalle: [],
    lineasDetalle: [],
    cilindrajesDetalle: []
  });
  const [slaStats, setSlaStats] = useState([]);
  const [selectedZonaFilter, setSelectedZonaFilter] = useState('todas');
  // Filtro por concesionario de "Especificaciones de Vehículos Vendidos" (solo superadmin)
  const [specConcesionario, setSpecConcesionario] = useState('todos');
  const [specData, setSpecData] = useState(null);
  const [specLoading, setSpecLoading] = useState(false);
  // Filtro por concesionario de "Movimiento por Entidad" (solo superadmin)
  const [entConcesionario, setEntConcesionario] = useState('todos');
  const [entData, setEntData] = useState(null);
  const [entLoading, setEntLoading] = useState(false);
  const [cuplModalOpen, setCuplModalOpen] = useState(false);
  const [cuplModalFilterType, setCuplModalFilterType] = useState('');
  const [cuplModalFilterTitle, setCuplModalFilterTitle] = useState('');

  const openTramitesModal = (filterType, filterTitle) => {
    setModalFilterType(filterType);
    setModalFilterTitle(filterTitle);
    setModalOpen(true);
  };

  const openCuplModal = (filterType, filterTitle) => {
    setCuplModalFilterType(filterType);
    setCuplModalFilterTitle(filterTitle);
    setCuplModalOpen(true);
  };

  // Recarga solo las especificaciones de vehículos cuando se filtra por concesionario (o cambian las fechas)
  useEffect(() => {
    if (user?.role !== 'superadmin' || specConcesionario === 'todos') { setSpecData(null); return undefined; }
    let cancelado = false;
    setSpecLoading(true);
    const p = new URLSearchParams();
    if (startDate) p.append('startDate', startDate);
    if (endDate) p.append('endDate', endDate);
    p.append('concesionario_id', specConcesionario);
    axios.get(`/api/tramites/stats/vehicle-specs?${p.toString()}`)
      .then(r => { if (!cancelado) setSpecData(r.data); })
      .catch(() => { if (!cancelado) setSpecData({ marcasDetalle: [], lineasDetalle: [], cilindrajesDetalle: [] }); })
      .finally(() => { if (!cancelado) setSpecLoading(false); });
    return () => { cancelado = true; };
  }, [specConcesionario, startDate, endDate, user]);
  const specSource = (specConcesionario !== 'todos' && specData) ? specData : entityMovement;

  // Movimiento por entidad filtrado por concesionario
  useEffect(() => {
    if (user?.role !== 'superadmin' || entConcesionario === 'todos') { setEntData(null); return undefined; }
    let cancelado = false;
    setEntLoading(true);
    const p = new URLSearchParams();
    if (startDate) p.append('startDate', startDate);
    if (endDate) p.append('endDate', endDate);
    p.append('concesionario_id', entConcesionario);
    axios.get(`/api/tramites/stats/entity-chart?${p.toString()}`)
      .then(r => { if (!cancelado) setEntData(r.data); })
      .catch(() => { if (!cancelado) setEntData({ transitos: [], concesionarios: [], puntosDeVenta: [] }); })
      .finally(() => { if (!cancelado) setEntLoading(false); });
    return () => { cancelado = true; };
  }, [entConcesionario, startDate, endDate, user]);
  const entSource = (entConcesionario !== 'todos' && entData) ? entData : entityMovement;

  const fetchDashboardData = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) {
        setLoading(true);
      }

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const hasCupl = user?.role === 'superadmin' || user?.modules?.some(m => m.name === 'cupl-payments');

      const promises = [
        axios.get(`/api/transitos/stats/plates?${params.toString()}`),
        axios.get(`/api/cash/balance?${params.toString()}`),
        axios.get(`/api/tramites/stats/pending-preassignment`),
        axios.get(`/api/novelties/stats/pending-count`),
        axios.get(`/api/range-requests/stats/pending-count`),
      ];

      let deletionPromiseIndex = -1;
      if (user?.role === 'superadmin' || user?.role === 'transito' || user?.role === 'validador' || !!user?.transito_id) {
        deletionPromiseIndex = promises.length;
        promises.push(axios.get(`/api/tramites/deletion-requests`));
      }

      let cambioPlacaPromiseIndex = promises.length;
      promises.push(axios.get(`/api/plate-change/requests?status=pendiente`));

      let entityMovementIndex = -1;
      if (user?.role === 'superadmin' || user?.concesionario_id || user?.punto_venta_id || user?.transito_id) {
        entityMovementIndex = promises.length;
        promises.push(axios.get(`/api/tramites/stats/entity-movement?${params.toString()}`));
      }

      let slaPromiseIndex = -1;
      if (user?.role === 'superadmin') {
        slaPromiseIndex = promises.length;
        promises.push(axios.get(`/api/transitos/stats/sla-preassignment?${params.toString()}`));
      }

      const alertsPromiseIndex = promises.length;
      promises.push(axios.get(`/api/transitos/alerts/active`));

      let cambiosTipoIndex = -1;
      if (user?.role === 'superadmin' || user?.profile_name === 'superadmin') {
        cambiosTipoIndex = promises.length;
        promises.push(axios.get(`/api/tramites/cambios-tipo/count`).catch(() => ({ data: { total: 0 } })));
      }

      let cuplStatsPromiseIndex = -1;
      let cuplRecentPromiseIndex = -1;
      let cuplChartPromiseIndex = -1;

      if (hasCupl) {
        cuplStatsPromiseIndex = promises.length;
        promises.push(cuplAPI.getStatistics(params.toString() ? `?${params.toString()}` : ''));
        
        cuplRecentPromiseIndex = promises.length;
        promises.push(axios.get(`/api/cupl?${params.toString()}&limit=5`));
        
        cuplChartPromiseIndex = promises.length;
        promises.push(cuplAPI.getChartStats(params.toString() ? `?${params.toString()}` : ''));
      }

      const results = await Promise.all(promises);

      setTramiteStats(results[0].data);
      if (results[0].headers && results[0].headers['x-has-assigned-plates'] !== undefined) {
        setHasAssignedPlates(results[0].headers['x-has-assigned-plates'] === 'true');
      }
      setBalance(results[1].data.balance || 0);
      setPendingCount(results[2].data.count || 0);
      setTotalTramites(results[2].data.total || 0);
      setEsperandoPreasignacion(results[2].data.esperandoPreasignacion || 0);
      setEsperandoSoat(results[2].data.esperandoSoat || 0);
      setEsperandoCupl(results[2].data.esperandoCupl || 0);
      setTienenSoat(results[2].data.tienenSoat || 0);
      setListosParaFinalizar(results[2].data.listosParaFinalizar || 0);
      setOtrosTramites(results[2].data.otrosTramites || 0);
      setIntentosEjecucion(results[2].data.intentosEjecucion || 0);
      setPendingNovelties(results[3].data.count || 0);
      setPendingRangeRequests(results[4].data.count || 0);

      if (deletionPromiseIndex !== -1 && results[deletionPromiseIndex]) {
        setPendingDeletionsCount(results[deletionPromiseIndex].data.length || 0);
      }

      if (cambioPlacaPromiseIndex !== -1 && results[cambioPlacaPromiseIndex]) {
        setPendingCambioPlacaCount(results[cambioPlacaPromiseIndex].data.length || 0);
      }

      if (entityMovementIndex !== -1 && results[entityMovementIndex]) {
        setEntityMovement(results[entityMovementIndex].data || { transitos: [], concesionarios: [], puntosDeVenta: [], zonas: [], zonasDetalle: [] });
      }

      if (slaPromiseIndex !== -1 && results[slaPromiseIndex]) {
        setSlaStats(results[slaPromiseIndex].data || []);
      }

      if (results[alertsPromiseIndex]) {
        setTransitoAlerts(results[alertsPromiseIndex].data || []);
      }

      if (cambiosTipoIndex !== -1 && results[cambiosTipoIndex]) {
        setCambiosTipoCount(results[cambiosTipoIndex].data?.total || 0);
      }

      if (hasCupl) {
        setStats(results[cuplStatsPromiseIndex].data);
        setRecentCUPLs(Array.isArray(results[cuplRecentPromiseIndex].data) ? results[cuplRecentPromiseIndex].data.slice(0, 5) : []);
        setChartData(results[cuplChartPromiseIndex].data);
      } else {
        setStats(null);
        setRecentCUPLs([]);
        setChartData({ paidCupls: [], income: [] });
      }
    } catch (err) {
      console.error('Error al cargar datos del dashboard:', err);
    } finally {
      if (!isSilent) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, user]);

  useEffect(() => {
    fetchDashboardData(false);
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 30000); // Refresco automático en vivo cada 30 segundos (silencioso sin parpadeo)

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const setFilterRange = (type) => {
    const today = new Date();
    let start = new Date();

    if (type === 'day') {
      start = today;
    } else if (type === 'week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Lunes
      start = new Date(today.setDate(diff));
    } else if (type === 'month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setActiveFilter(type);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`Fecha: ${label}`}</p>
          <p className="value">
            {payload[0].name === 'total'
              ? `Ingreso: ${formatCurrency(payload[0].value)}`
              : `Pagados: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const totalPaidInChart = Array.isArray(chartData?.paidCupls) 
    ? chartData.paidCupls.reduce((acc, curr) => acc + parseInt(curr.count || 0), 0)
    : 0;

  const totalIncomeInChart = Array.isArray(chartData?.income) 
    ? chartData.income.reduce((acc, curr) => acc + parseFloat(curr.total || 0), 0)
    : 0;

  // Determinar si el usuario es administrador de la agencia (ve saldo y graficos de ingresos)
  const isAgencyAdmin = user?.role === 'superadmin' || (!user?.concesionario_id && !user?.transito_id);
  const totalPlacasDisponibles = tramiteStats.reduce((acc, curr) => {
    return acc + (user?.role === 'superadmin' ? (curr.asignadas_transiapp || 0) : (curr.disponibles || 0));
  }, 0);
  const lowPlatesList = tramiteStats.filter(stat => {
    if (!stat.vehicle_type) return false;
    const qty = user?.role === 'superadmin' ? (stat.asignadas_transiapp || 0) : (stat.disponibles || 0);
    return qty <= 10 && qty > 0;
  });

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="📊 Dashboard" />

        <div className="page-content">
          {/* Filtros de Fecha Rápidos */}
          <div className="dashboard-controls">
            <div className="quick-filters">
              <button
                className={`btn-filter ${activeFilter === 'day' ? 'active' : ''}`}
                onClick={() => setFilterRange('day')}
              >
                Hoy
              </button>
              <button
                className={`btn-filter ${activeFilter === 'week' ? 'active' : ''}`}
                onClick={() => setFilterRange('week')}
              >
                Esta Semana
              </button>
              <button
                className={`btn-filter ${activeFilter === 'month' ? 'active' : ''}`}
                onClick={() => setFilterRange('month')}
              >
                Este Mes
              </button>
            </div>

            <div className="dashboard-filters">
              <div className="filter-group">
                <label>Desde:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setActiveFilter('custom');
                  }}
                />
              </div>
              <div className="filter-group">
                <label>Hasta:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setActiveFilter('custom');
                  }}
                />
              </div>
              <button className="btn-refresh" onClick={fetchDashboardData}>
                🔄 Actualizar
              </button>
            </div>
          </div>

          {/* Alerta de solicitudes de cambio de placa / traslados pendientes */}
          {pendingCambioPlacaCount > 0 && (
            <div className="alert-banner info" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              backgroundColor: '#f5f3ff',
              border: '1px solid #ddd6fe',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#5b21b6',
              fontSize: '15px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>📩</span>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#4c1d95', marginBottom: '2px' }}>
                    {(user?.role === 'superadmin' || user?.profile_name === 'superadmin' || user?.role === 'admin')
                      ? 'ℹ️ Alerta TransiApp - Solicitudes de Cambio de Placa / Traslado Pendientes'
                      : (user?.transito_id || user?.role === 'transito' || user?.role === 'admin_transito')
                      ? '📩 Solicitudes Pendientes de Aprobación en Tránsito'
                      : '📩 Solicitudes de Cambio de Placa / Traslado Enviadas'}
                  </div>
                  <div style={{ color: '#5b21b6', fontSize: '14px' }}>
                    {(user?.role === 'superadmin' || user?.profile_name === 'superadmin' || user?.role === 'admin')
                      ? <>Hay <strong>{pendingCambioPlacaCount}</strong> {pendingCambioPlacaCount === 1 ? 'solicitud' : 'solicitudes'} de <strong>cambio de placa / traslado de tránsito</strong> pendiente{pendingCambioPlacaCount === 1 ? '' : 's'} de aprobación por los Organismos de Tránsito.</>
                      : (user?.transito_id || user?.role === 'transito' || user?.role === 'admin_transito')
                      ? <>Hay <strong>{pendingCambioPlacaCount}</strong> {pendingCambioPlacaCount === 1 ? 'solicitud' : 'solicitudes'} de <strong>cambio de placa / eliminación por traslado de tránsito</strong> pendiente{pendingCambioPlacaCount === 1 ? '' : 's'} de aprobación en su Tránsito.</>
                      : <>Tiene <strong>{pendingCambioPlacaCount}</strong> {pendingCambioPlacaCount === 1 ? 'solicitud' : 'solicitudes'} de <strong>cambio de placa / traslado de tránsito</strong> pendiente{pendingCambioPlacaCount === 1 ? '' : 's'} de respuesta por el Tránsito.</>}
                  </div>
                </div>
              </div>
              <button 
                onClick={() => navigate((user?.concesionario_id || user?.punto_venta_id) ? '/mis-tramites' : '/bandeja-transito')} 
                className="btn-primary" 
                style={{
                  marginLeft: '16px',
                  padding: '8px 18px',
                  fontSize: '13px',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.2s'
                }}
              >
                {(user?.concesionario_id || user?.punto_venta_id) ? 'Ver Mis Trámites ➡️' : 'Ir a Bandeja Tránsito ➡️'}
              </button>
            </div>
          )}

          {/* Alerta de trámites radicados sin preasignar para tránsito */}
          {pendingCount > 0 && user?.transito_id && (
            <div className="alert-banner warning" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#92400e',
              fontSize: '15px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <span>
                  Hay <strong>{pendingCount}</strong> {pendingCount === 1 ? 'trámite radicado' : 'trámites radicados'} pendiente{pendingCount === 1 ? '' : 's'} por preasignar placa.
                </span>
              </div>
              <button 
                onClick={() => navigate(user?.transito_id ? '/bandeja-transito' : '/mis-tramites')} 
                className="btn-primary" 
                style={{
                  marginLeft: 'auto',
                  padding: '6px 16px',
                  fontSize: '13px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#b45309'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#d97706'}
              >
                {user?.transito_id ? 'Ir a Bandeja de Tránsito ➡️' : 'Ver mis Trámites ➡️'}
              </button>
            </div>
          )}

          {/* Alertas de Tránsitos con Novedades */}
          {transitoAlerts.length > 0 && transitoAlerts.map(alert => (
            <div key={alert.id} className="alert-banner warning" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '16px 24px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#92400e',
              fontSize: '15px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>📢</span>
                <span>
                  <strong>Atención {alert.name}:</strong> {alert.novedad_actual}
                </span>
              </div>
            </div>
          ))}

          {/* Alerta de solicitudes de rango pendientes */}
          {pendingRangeRequests > 0 && (
            <div className="alert-banner warning" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#1e40af',
              fontSize: '15px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>📋</span>
                <span>
                  Tienes <strong>{pendingRangeRequests}</strong> {pendingRangeRequests === 1 ? 'solicitud' : 'solicitudes'} de rango de placas pendiente{pendingRangeRequests === 1 ? '' : 's'} por revisar.
                </span>
              </div>
              <button 
                onClick={() => navigate('/range-requests')} 
                className="btn-primary" 
                style={{
                  marginLeft: 'auto',
                  padding: '6px 16px',
                  fontSize: '13px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
              >
                Revisar Solicitudes ➡️
              </button>
            </div>
          )}

          {/* Alerta de solicitudes de eliminación pendientes */}
          {pendingDeletionsCount > 0 && (
            <div className="alert-banner danger" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#991b1b',
              fontSize: '15px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🗑️</span>
                <span>
                  Hay <strong>{pendingDeletionsCount}</strong> {pendingDeletionsCount === 1 ? 'solicitud' : 'solicitudes'} de eliminación de trámite pendiente{pendingDeletionsCount === 1 ? '' : 's'}.
                </span>
              </div>
              <button 
                onClick={() => navigate('/bandeja-transito')} 
                className="btn-primary" 
                style={{
                  marginLeft: 'auto',
                  padding: '6px 16px',
                  fontSize: '13px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                Ir a Bandeja Tránsito ➡️
              </button>
            </div>
          )}

          {/* Alerta de Placas Bajas para Superadmin */}
          {user?.role === 'superadmin' && lowPlatesList.length > 0 && (
            <div className="alert-banner danger" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#991b1b',
              fontSize: '15px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🚨</span>
                <div>
                  <strong>Atención - Inventario Bajo de Placas:</strong> 
                  <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                    {lowPlatesList.map((stat, idx) => {
                      const qty = user?.role === 'superadmin' ? (stat.asignadas_transiapp || 0) : stat.disponibles;
                      return (
                        <li key={idx}>
                          <strong>{stat.transito_name}</strong>: {qty} placa(s) disponibles para {stat.vehicle_type === 'moto' ? 'Moto' : stat.vehicle_type === 'moto_carro' ? 'Moto Carro' : 'Carro'}.
                        </li>
                      );
                    })}
                  </ul>
                  <div style={{ marginTop: '5px' }}>Se recomienda solicitar más rangos al tránsito correspondiente.</div>
                </div>
              </div>
              <button 
                onClick={() => navigate('/plates')} 
                className="btn-primary" 
                style={{
                  marginLeft: 'auto',
                  padding: '6px 16px',
                  fontSize: '13px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                Revisar Inventario ➡️
              </button>
            </div>
          )}

          {/* Tarjetas de Resumen Principal */}
          <div className="stats-grid">
            {isAgencyAdmin ? (
              <>
                <div className="stat-card primary">
                  <div className="stat-icon">💰</div>
                  <div className="stat-details">
                    <span className="stat-label">Saldo en Caja</span>
                    <span className="stat-value">{formatCurrency(balance)}</span>
                  </div>
                </div>
                {user?.role === 'superadmin' && (
                  <div className={`stat-card ${lowPlatesList.length > 0 ? 'danger' : 'info'}`}>
                    <div className="stat-icon">🆔</div>
                    <div className="stat-details" style={{ flex: 1 }}>
                      <span className="stat-label">Placas Disponibles</span>
                      <span className="stat-value">
                        {totalPlacasDisponibles}
                      </span>
                    </div>
                    <button 
                      className="btn-text" 
                      onClick={() => navigate('/plates')}
                      style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                    >
                      Ver 🔍
                    </button>
                  </div>
                )}
              </>
            ) : (
              (user?.role !== 'concesionario' && !user?.concesionario_id) || hasAssignedPlates ? (
                <div className="stat-card primary">
                  <div className="stat-icon">🆔</div>
                  <div className="stat-details">
                    <span className="stat-label">Placas Disponibles</span>
                    <span className="stat-value">
                      {totalPlacasDisponibles}
                    </span>
                  </div>
                </div>
              ) : null
            )}

            {isAgencyAdmin && (
              <div className="stat-card danger">
                <div className="stat-icon">💸</div>
                <div className="stat-details">
                  <span className="stat-label">Gastos CUPLs Pagados</span>
                  <span className="stat-value">{formatCurrency(stats?.total_gastos_pagados || 0)}</span>
                </div>
              </div>
            )}

            {user?.role === 'superadmin' || user?.modules?.some(m => m.name === 'cupl-payments') ? (
              <>
                <div className="stat-card success">
                  <div className="stat-icon">✅</div>
                  <div className="stat-details" style={{ flex: 1 }}>
                    <span className="stat-label">CUPLs Pagados</span>
                    <span className="stat-value">{stats?.total_pagados || 0}</span>
                    <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px', fontWeight: '500' }}>
                      📋 De mis trámites: <strong>{stats?.total_pagados_mis_tramites || 0}</strong> | 📥 Directos/Correo: <strong>{stats?.total_pagados_directos || 0}</strong>
                    </div>
                  </div>
                  {stats?.total_pagados > 0 && (
                    <button 
                      className="btn-text" 
                      onClick={() => openCuplModal('pagados', 'CUPLs Pagados')}
                      style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                    >
                      Ver 🔍
                    </button>
                  )}
                </div>

                <div className="stat-card warning">
                  <div className="stat-icon">⏳</div>
                  <div className="stat-details" style={{ flex: 1 }}>
                    <span className="stat-label">CUPLs Pendientes</span>
                    <span className="stat-value">{stats?.total_pendientes || 0}</span>
                  </div>
                  {stats?.total_pendientes > 0 && (
                    <button 
                      className="btn-text" 
                      onClick={() => openCuplModal('pendientes', 'CUPLs Pendientes')}
                      style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                    >
                      Ver 🔍
                    </button>
                  )}
                </div>

                <div className="stat-card danger">
                  <div className="stat-icon">🚫</div>
                  <div className="stat-details" style={{ flex: 1 }}>
                    <span className="stat-label">CUPLs Vencidos</span>
                    <span className="stat-value">{stats?.total_vencidos || 0}</span>
                  </div>
                  {stats?.total_vencidos > 0 && (
                    <button 
                      className="btn-text" 
                      onClick={() => openCuplModal('vencidos', 'CUPLs Vencidos')}
                      style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                    >
                      Ver 🔍
                    </button>
                  )}
                </div>
              </>
            ) : null}

            <div className="stat-card info">
              <div className="stat-icon">📑</div>
              <div className="stat-details">
                <span className="stat-label">Total Trámites</span>
                <span className="stat-value">
                  {totalTramites}
                </span>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">🕒</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label">Esp. Preasignación</span>
                <span className="stat-value">{esperandoPreasignacion}</span>
              </div>
              {esperandoPreasignacion > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => openTramitesModal('esperandoPreasignacion', 'Esperando Preasignación')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>

            <div className="stat-card danger">
              <div className="stat-icon">📄</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label">Esp. SOAT</span>
                <span className="stat-value">{esperandoSoat}</span>
              </div>
              {esperandoSoat > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => openTramitesModal('esperandoSoat', 'Esperando SOAT')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>

            {(!user?.concesionario_id && user?.role !== 'concesionario') && (
              <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b', backgroundColor: '#fffbeb' }}>
                <div className="stat-icon">💳</div>
                <div className="stat-details" style={{ flex: 1 }}>
                  <span className="stat-label" style={{ color: '#b45309' }}>Esp. CUPL</span>
                  <span className="stat-value" style={{ color: '#f59e0b' }}>{esperandoCupl}</span>
                </div>
                {esperandoCupl > 0 && (
                  <button 
                    className="btn-text" 
                    onClick={() => openTramitesModal('esperandoCupl', 'Esperando CUPL')}
                    style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#b45309' }}
                  >
                    Ver 🔍
                  </button>
                )}
              </div>
            )}

            <div className="stat-card success">
              <div className="stat-icon">🛡️</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label">Con SOAT Vigente</span>
                <span className="stat-value">{tienenSoat}</span>
              </div>
              {tienenSoat > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => openTramitesModal('tienenSoat', 'Con SOAT Vigente')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>

            <div className="stat-card primary" style={{ backgroundColor: '#eff6ff', borderLeft: '4px solid #3b82f6' }}>
              <div className="stat-icon">🎯</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label" style={{ color: '#1e40af' }}>Listos para Finalizar</span>
                <span className="stat-value" style={{ color: '#2563eb' }}>{listosParaFinalizar}</span>
              </div>
              {listosParaFinalizar > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => openTramitesModal('listosParaFinalizar', 'Trámites Listos para Finalizar')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#1d4ed8' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>

            {(canSeeCambiosTramites || !!user?.transito_id || user?.role === 'transito' || otrosTramites > 0) && (
              <div className="stat-card" style={{ borderLeft: '4px solid #7c3aed', backgroundColor: '#f5f3ff' }}>
                <div className="stat-icon">📂</div>
                <div className="stat-details" style={{ flex: 1 }}>
                  <span className="stat-label" style={{ color: '#5b21b6' }}>Otros Trámites</span>
                  <span className="stat-value" style={{ color: '#7c3aed' }}>{otrosTramites}</span>
                </div>
                {otrosTramites > 0 && (
                  <button
                    className="btn-text"
                    onClick={() => (canSeeCambiosTramites || !!user?.transito_id || user?.role === 'transito')
                    ? navigate('/bandeja-transito', { state: { pestana: 'otros' } })
                    : openTramitesModal('otrosTramites', 'Otros Trámites (inscripción de prenda, duplicado de licencia)')}
                    style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#5b21b6' }}
                  >
                    Ver 🔍
                  </button>
                )}
              </div>
            )}

            <div className="stat-card" style={{ borderLeft: '4px solid #d97706', backgroundColor: '#fffbeb' }}>
              <div className="stat-icon">⛔</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label" style={{ color: '#92400e' }}>Intentos de Ejecución</span>
                <span className="stat-value" style={{ color: '#d97706' }}>{intentosEjecucion}</span>
              </div>
              {intentosEjecucion > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => openTramitesModal('intentosEjecucion', 'Trámites con Intentos de Ejecución No Exitosos')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#92400e' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>

            {canSeeCambiosTramites && (
              <div className="stat-card" style={{ borderLeft: '4px solid #0891b2', backgroundColor: '#ecfeff' }}>
                <div className="stat-icon">🔄</div>
                <div className="stat-details" style={{ flex: 1 }}>
                  <span className="stat-label" style={{ color: '#155e75' }}>Cambios Trámites por revisar</span>
                  <span className="stat-value" style={{ color: '#0891b2' }}>{cambiosTipoCount}</span>
                </div>
                {cambiosTipoCount > 0 && (
                  <button 
                    className="btn-text" 
                    onClick={() => navigate('/cambios-tramites')}
                    style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#155e75' }}
                  >
                    Ver 🔍
                  </button>
                )}
              </div>
            )}

            <div className="stat-card" style={{ borderLeft: '4px solid #ef4444', backgroundColor: '#fef2f2' }}>
              <div className="stat-icon">⚠️</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label" style={{ color: '#b91c1c' }}>Novedades Pendientes</span>
                <span className="stat-value" style={{ color: '#ef4444' }}>{pendingNovelties}</span>
              </div>
              {pendingNovelties > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => openTramitesModal('novedadesPendientes', 'Novedades Pendientes')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#b91c1c' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>

            <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6', backgroundColor: '#f5f3ff' }}>
              <div className="stat-icon">📩</div>
              <div className="stat-details" style={{ flex: 1 }}>
                <span className="stat-label" style={{ color: '#6d28d9' }}>Solicitudes Cambio Placa</span>
                <span className="stat-value" style={{ color: '#7c3aed' }}>{pendingCambioPlacaCount}</span>
              </div>
              {pendingCambioPlacaCount > 0 && (
                <button 
                  className="btn-text" 
                  onClick={() => navigate((user?.concesionario_id || user?.punto_venta_id) ? '/mis-tramites' : '/bandeja-transito')}
                  style={{ alignSelf: 'flex-end', marginLeft: 'auto', fontSize: '12px', color: '#6d28d9' }}
                >
                  Ver 🔍
                </button>
              )}
            </div>
          </div>

          {user?.role !== 'superadmin' && (user?.modules?.some(m => m.name === 'cupl-payments') || (!user?.concesionario_id && !user?.transito_id && user?.role !== 'superadmin')) && (
            <div className="dashboard-charts-grid" style={{ gridTemplateColumns: (user?.modules?.some(m => m.name === 'cupl-payments') && (!user?.concesionario_id && !user?.transito_id)) ? '1fr 1fr' : '1fr' }}>
              {user?.modules?.some(m => m.name === 'cupl-payments') && (
                <div className="dashboard-section chart-section">
                  <div className="section-header">
                    <h3>📈 CUPLs Pagados (Total: {totalPaidInChart})</h3>
                  </div>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData.paidCupls}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--sidebar-border)" />
                        <XAxis
                          dataKey="date"
                          stroke="var(--secondary-color)"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="var(--secondary-color)"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="count"
                          fill="var(--primary-color)"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {(!user?.concesionario_id && !user?.transito_id && user?.role !== 'superadmin') && (
                <div className="dashboard-section chart-section">
                  <div className="section-header">
                    <h3>💵 Ingresos (Total: {formatCurrency(totalIncomeInChart)})</h3>
                  </div>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={chartData.income}>
                        <defs>
                          <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--sidebar-border)" />
                        <XAxis
                          dataKey="date"
                          stroke="var(--secondary-color)"
                          fontSize={12}
                          tickLine={false}
                          tickFormatter={(value) => value}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="var(--secondary-color)"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => `$${value / 1000}k`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="total"
                          stroke="#10b981"
                          fillOpacity={1}
                          fill="url(#colorIncome)"
                          strokeWidth={3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gráficas de Movimiento por Entidad para Superadmin */}
          {user?.role === 'superadmin' && entityMovement && (
            <div className="dashboard-section" style={{ marginTop: '25px', padding: '24px', backgroundColor: 'var(--card-bg)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', border: '1px solid var(--sidebar-border)' }}>
              <div className="section-header" style={{ marginBottom: '20px' }}>
                <h3>📊 Movimiento por Entidad (Tránsitos, Concesionarios y Puntos de Venta)</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <label htmlFor="ent-conc" style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>🏢 Concesionario:</label>
                  <select
                    id="ent-conc"
                    value={entConcesionario}
                    onChange={(e) => setEntConcesionario(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--sidebar-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}
                  >
                    <option value="todos">Todos los concesionarios</option>
                    {(entityMovement?.concesionarios || []).map(c => (
                      <option key={c.id ?? 'directo'} value={c.id ?? 'directo'}>{c.name}</option>
                    ))}
                  </select>
                  {entLoading && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>⏳ Cargando...</span>}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {/* 1. Movimiento por Organismo de Tránsito */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🏛️ Movimiento por Tránsito
                  </h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={entSource?.transitos || []} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                      <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} />
                      <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={110} tick={{ fill: 'var(--text-primary)' }} />
                      <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* 2. Movimiento por Concesionario */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🏢 Movimiento por Concesionario
                  </h4>
                  <ResponsiveContainer width="100%" height={Math.max(260, (entSource?.concesionarios?.length || 0) * 28 + 40)}>
                    <BarChart data={entSource?.concesionarios || []} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                      <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} allowDecimals={false} />
                      {/* interval={0}: mostrar el nombre de TODOS los concesionarios (antes se ocultaban algunos) */}
                      <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={130} interval={0} tickFormatter={(v) => truncateLabel(v)} tick={{ fill: 'var(--text-primary)' }} />
                      <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                      <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* 3. Movimiento por Punto de Venta */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📍 Movimiento por Punto de Venta
                  </h4>
                  <div style={{ maxHeight: '420px', overflowY: 'auto', overflowX: 'hidden' }}>
                    <ResponsiveContainer width="100%" height={barChartHeight(entSource?.puntosDeVenta)}>
                      <BarChart data={entSource?.puntosDeVenta || []} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                        <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={170} interval={0} tickFormatter={(v) => truncateLabel(v)} tick={{ fill: 'var(--text-primary)' }} />
                        <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={18} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Análisis Geográfico y por Zonas para Superadmin */}
          {user?.role === 'superadmin' && entityMovement?.zonas && entityMovement.zonas.length > 0 && (
            <div className="dashboard-section" style={{ marginTop: '25px', padding: '24px', backgroundColor: 'var(--card-bg)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', border: '1px solid var(--sidebar-border)' }}>
              <div className="section-header" style={{ marginBottom: '20px' }}>
                <h3>📍 Análisis Geográfico y por Zonas</h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {/* 1. Movimiento General por Zona */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📊 Movimiento General por Zona
                  </h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={entityMovement.zonas || []} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                      <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} />
                      <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={130} tick={{ fill: 'var(--text-primary)' }} />
                      <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                      <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* 2. Top Concesionarios por Zona (Pastel) */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🍩 Composición por Concesionario
                  </h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={(entityMovement.zonasDetalle || []).reduce((acc, current) => {
                          const existing = acc.find(item => item.name === current.concesionario);
                          if (existing) {
                            existing.value += current.count;
                          } else {
                            acc.push({ name: current.concesionario, value: current.count });
                          }
                          return acc;
                        }, [])}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {
                          (entityMovement.zonasDetalle || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6'][index % 6]} />
                          ))
                        }
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} trámites`, 'Volumen']} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* 3. Puntos de Venta por Zona */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                      📍 Top Puntos de Venta por Zona
                    </h4>
                    <select 
                      value={selectedZonaFilter} 
                      onChange={(e) => setSelectedZonaFilter(e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--sidebar-border)', fontSize: '12px', outline: 'none', maxWidth: '140px' }}
                    >
                      <option value="ALL">Todas las Zonas</option>
                      {(entityMovement.zonas || []).map(z => (
                        <option key={z.name} value={z.name}>{z.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ maxHeight: '260px', overflowY: 'auto', paddingRight: '10px' }}>
                    {(entityMovement.zonasDetalle || [])
                      .filter(detalle => selectedZonaFilter === 'ALL' || detalle.zona === selectedZonaFilter)
                      .map((detalle, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '10px',
                        borderBottom: '1px solid var(--sidebar-border)',
                        backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>{detalle.punto_venta}</span>
                          <span style={{ fontSize: '11px', color: 'var(--secondary-color)' }}>{detalle.zona} - {detalle.concesionario}</span>
                        </div>
                        <span style={{ 
                          fontWeight: '700', 
                          fontSize: '13px', 
                          color: '#3b82f6',
                          backgroundColor: '#eff6ff',
                          padding: '4px 8px',
                          borderRadius: '6px'
                        }}>
                          {detalle.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Análisis de Especificaciones de Vehículos Vendidos (Para Todos excepto Tránsitos) */}
          {!(user?.transito_id || user?.role === 'transito') && (entityMovement?.marcasDetalle?.length > 0 || entityMovement?.lineasDetalle?.length > 0 || specConcesionario !== 'todos') && (
            <div className="dashboard-section" style={{ marginTop: '25px', padding: '24px', backgroundColor: 'var(--card-bg)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', border: '1px solid var(--sidebar-border)' }}>
              <div className="section-header" style={{ marginBottom: '20px' }}>
                <h3>🏍️ Especificaciones de Vehículos Vendidos</h3>
                {user?.role === 'superadmin' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <label htmlFor="spec-conc" style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>🏢 Concesionario:</label>
                    <select
                      id="spec-conc"
                      value={specConcesionario}
                      onChange={(e) => setSpecConcesionario(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--sidebar-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}
                    >
                      <option value="todos">Todos los concesionarios</option>
                      {(entityMovement?.concesionarios || []).map(c => (
                        <option key={c.id ?? 'directo'} value={c.id ?? 'directo'}>{c.name}</option>
                      ))}
                    </select>
                    {specLoading && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>⏳ Cargando...</span>}
                  </div>
                )}
              </div>
              {specConcesionario !== 'todos' && !specLoading && !(specSource?.marcasDetalle?.length > 0) && (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>Este concesionario no tiene trámites en el rango de fechas seleccionado.</p>
              )}
              
              {/* Agrupamos por Zona */}
              {Object.keys(
                (specSource.marcasDetalle || []).reduce((acc, curr) => {
                  const group = curr.zona || 'Mi Punto de Venta';
                  acc[group] = true;
                  return acc;
                }, {})
              ).map(zonaName => {
                // Filtrar datos para esta zona
                const filterByZona = (arr) => (arr || []).filter(item => (item.zona || 'Mi Punto de Venta') === zonaName);
                const marcas = filterByZona(specSource.marcasDetalle);
                const lineas = filterByZona(specSource.lineasDetalle);
                const cilindrajes = filterByZona(specSource.cilindrajesDetalle);

                return (
                  <div key={zonaName} style={{ marginBottom: '30px' }}>
                    {zonaName !== 'Mi Punto de Venta' && zonaName !== 'Mi Tránsito' && (
                      <h4 style={{ fontSize: '15px', color: 'var(--primary-color)', marginBottom: '15px', borderBottom: '1px solid var(--sidebar-border)', paddingBottom: '8px' }}>
                        📍 {zonaName}
                      </h4>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                      
                      {/* Marcas Card */}
                      <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                        <h4 style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '15px' }}>🏷️ Marcas Vendidas</h4>
                        <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
                          {marcas.map((detalle, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid var(--sidebar-border)' }}>
                              <span style={{ fontWeight: '600', fontSize: '12px', color: 'var(--text-primary)' }}>{detalle.marca}</span>
                              <span style={{ fontWeight: '700', fontSize: '12px', color: '#8b5cf6', backgroundColor: '#f3e8ff', padding: '3px 8px', borderRadius: '6px' }}>{detalle.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Lineas Card */}
                      <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                        <h4 style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '15px' }}>🏍️ Líneas Vendidas</h4>
                        <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
                          {lineas.map((detalle, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid var(--sidebar-border)' }}>
                              <span style={{ fontWeight: '600', fontSize: '12px', color: 'var(--text-primary)' }}>{detalle.linea}</span>
                              <span style={{ fontWeight: '700', fontSize: '12px', color: '#3b82f6', backgroundColor: '#eff6ff', padding: '3px 8px', borderRadius: '6px' }}>{detalle.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Cilindrajes Card */}
                      <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                        <h4 style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '15px' }}>⚙️ Cilindrajes</h4>
                        <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
                          {cilindrajes.map((detalle, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid var(--sidebar-border)' }}>
                              <span style={{ fontWeight: '600', fontSize: '12px', color: 'var(--text-primary)' }}>{detalle.cilindraje} cc</span>
                              <span style={{ fontWeight: '700', fontSize: '12px', color: '#f59e0b', backgroundColor: '#fef3c7', padding: '3px 8px', borderRadius: '6px' }}>{detalle.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Métrica SLA de Tiempo de Respuesta por Tránsito para Superadmin */}
          {user?.role === 'superadmin' && slaStats?.length > 0 && (
            <div className="dashboard-section" style={{ marginTop: '25px', padding: '24px', backgroundColor: 'var(--card-bg)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', border: '1px solid var(--sidebar-border)' }}>
              <div className="section-header" style={{ marginBottom: '6px' }}>
                <h3>⏱️ Tiempo Promedio de Respuesta por Tránsito (Radicado ➡️ Preasignado)</h3>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--secondary-color)', marginBottom: '20px' }}>
                Medición del tiempo transcurrido desde que un trámite es creado hasta que el Organismo de Tránsito le asigna placa.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                {slaStats.map((item) => {
                  const hours = item.promedio_horas;
                  const days = item.promedio_dias;
                  let badgeBg = '#dcfce7';
                  let badgeColor = '#15803d';
                  let badgeText = '🟢 Rápido (<6h)';
                  if (hours > 24) {
                    badgeBg = '#fee2e2';
                    badgeColor = '#b91c1c';
                    badgeText = '🔴 Lento (>24h)';
                  } else if (hours >= 6) {
                    badgeBg = '#fef3c7';
                    badgeColor = '#b45309';
                    badgeText = '🟡 Moderado (6h-24h)';
                  }

                  return (
                    <div key={item.transito_id} style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--sidebar-border)',
                      borderRadius: '12px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>
                          🏛️ {item.transito_name}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          backgroundColor: badgeBg,
                          color: badgeColor
                        }}>
                          {badgeText}
                        </span>
                      </div>

                      <div style={{ marginTop: '8px' }}>
                        <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary-color)' }}>
                          {hours < 24 ? `${hours} hrs` : `${days} días`}
                          <span style={{ fontSize: '12px', fontWeight: 'normal', color: 'var(--secondary-color)', marginLeft: '8px' }}>
                            ({hours} hrs en promedio)
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--secondary-color)', marginTop: '6px' }}>
                          📋 Trámites preasignados: <strong>{item.total_preasignados}</strong>
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', display: 'flex', gap: '12px' }}>
                          <span>⚡ Mínimo: <strong>{item.min_horas < 1 ? `${Math.round(item.min_horas * 60)} min` : `${item.min_horas}h`}</strong></span>
                          <span>🐢 Máximo: <strong>{item.max_horas > 24 ? `${(item.max_horas / 24).toFixed(1)} días` : `${item.max_horas}h`}</strong></span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tabla comparativa de SLA */}
              <div className="table-responsive" style={{ marginTop: '15px' }}>
                <table className="users-table" style={{ width: '100%', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>Organismo de Tránsito</th>
                      <th>Trámites Medidos</th>
                      <th>Tiempo Promedio (Horas)</th>
                      <th>Tiempo Promedio (Días)</th>
                      <th>Tiempo Mínimo</th>
                      <th>Tiempo Máximo</th>
                      <th>Nivel de Desempeño</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slaStats.map((item) => {
                      const hours = item.promedio_horas;
                      let badgeBg = '#dcfce7';
                      let badgeColor = '#15803d';
                      let badgeText = '🟢 Rápido (< 6h)';
                      if (hours > 24) {
                        badgeBg = '#fee2e2';
                        badgeColor = '#b91c1c';
                        badgeText = '🔴 Lento (> 24h)';
                      } else if (hours >= 6) {
                        badgeBg = '#fef3c7';
                        badgeColor = '#b45309';
                        badgeText = '🟡 Moderado (6h - 24h)';
                      }

                      return (
                        <tr key={item.transito_id}>
                          <td><strong>🏛️ {item.transito_name}</strong></td>
                          <td><strong>{item.total_preasignados}</strong> trámites</td>
                          <td><span style={{ fontWeight: '700', color: '#1e3a8a' }}>{item.promedio_horas} hrs</span></td>
                          <td>{item.promedio_dias} días</td>
                          <td>{item.min_horas < 1 ? `${Math.round(item.min_horas * 60)} min` : `${item.min_horas} hrs`}</td>
                          <td>{item.max_horas > 24 ? `${(item.max_horas / 24).toFixed(1)} días (${item.max_horas} hrs)` : `${item.max_horas} hrs`}</td>
                          <td>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              padding: '4px 10px',
                              borderRadius: '999px',
                              backgroundColor: badgeBg,
                              color: badgeColor
                            }}>
                              {badgeText}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {/* Gráficas de Movimiento por Puntos de Venta y Asesores para Concesionario */}
          {user?.role !== 'superadmin' && ['admin_concesionario', 'administrativo_concesionario'].includes(user?.profile_name) && (
            <div className="dashboard-section" style={{ marginTop: '25px', padding: '24px', backgroundColor: 'var(--card-bg)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', border: '1px solid var(--sidebar-border)' }}>
              <div className="section-header" style={{ marginBottom: '20px' }}>
                <h3>📍 Rendimiento por Puntos de Venta y Asesores</h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {/* 1. Movimiento por Punto de Venta */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🏪 Trámites por Punto de Venta
                  </h4>
                  <div style={{ maxHeight: '420px', overflowY: 'auto', overflowX: 'hidden' }}>
                    <ResponsiveContainer width="100%" height={barChartHeight(entityMovement?.puntosDeVenta)}>
                      <BarChart data={entityMovement?.puntosDeVenta || []} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                        <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={170} interval={0} tickFormatter={(v) => truncateLabel(v)} tick={{ fill: 'var(--text-primary)' }} />
                        <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={18} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Movimiento por Zona */}
                {(entityMovement?.zonas || []).length > 0 && (
                  <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                    <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🗺️ Trámites por Zona
                    </h4>
                    <ResponsiveContainer width="100%" height={Math.max(200, (entityMovement.zonas.length * 40) + 40)}>
                      <BarChart data={entityMovement.zonas} layout="vertical" margin={{ left: 20, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                        <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={140} tick={{ fill: 'var(--text-primary)' }} />
                        <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                        <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* 2. Movimiento por Asesor */}
                <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--sidebar-border)' }}>
                  <h4 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    👤 Trámites por Asesor / Usuario
                  </h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={entityMovement.asesores || []} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sidebar-border)" />
                      <XAxis type="number" stroke="var(--secondary-color)" fontSize={11} />
                      <YAxis dataKey="name" type="category" stroke="var(--secondary-color)" fontSize={11} width={140} tick={{ fill: 'var(--text-primary)' }} />
                      <Tooltip formatter={(value) => [`${value} trámites`, 'Trámites']} />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-main-grid" style={{ gridTemplateColumns: (user?.role === 'superadmin' || user?.modules?.some(m => m.name === 'cupl-payments')) ? '2fr 1fr' : '1fr' }}>
            {/* Gráfico/Resumen de Trámites/Placas por Organismo */}
            {user?.role === 'superadmin' && (
              <div className="dashboard-section main-section">
                <div className="section-header">
                  <h3>🏢 Inventario de Placas por Organismo</h3>
                  <button onClick={() => navigate('/plates')} className="btn-text">Ver todo →</button>
                </div>
                <div className="organism-list">
                  {tramiteStats.length > 0 ? (
                    tramiteStats.slice(0, 6).map((ts, idx) => {
                      const count = ts.count !== undefined ? ts.count : (ts.total || 0);
                      return (
                        <div key={idx} className="organism-item">
                          <div className="organism-info">
                            <span className="organism-name">{ts.transito_name}</span>
                            <span className="organism-type">
                              {ts.vehicle_type === 'moto' ? '🏍️ Moto' : 
                               ts.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : 
                               '🚗 Carro'}
                            </span>
                          </div>
                          <div className="organism-bar-container">
                            <div
                              className="organism-bar"
                              style={{ width: `${Math.min((count / 50) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <span className="organism-count">{count}</span>
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-data">No hay datos para el rango seleccionado</p>
                  )}
                </div>
              </div>
            )}

            {/* CUPLs Recientes */}
            {(user?.role === 'superadmin' || user?.modules?.some(m => m.name === 'cupl-payments')) && (
              <div className="dashboard-section">
                <div className="section-header">
                  <h3>🕒 Actividad Reciente</h3>
                  <button onClick={() => navigate('/cupl-payments')} className="btn-text">Ver todos →</button>
                </div>
                <div className="recent-list">
                  {recentCUPLs.length > 0 ? (
                    recentCUPLs.map((cupl) => (
                      <div key={cupl.id} className="recent-item">
                        <div className="recent-icon">📄</div>
                        <div className="recent-info">
                          <span className="recent-title">{cupl.plate} - {cupl.cupl_number}</span>
                          <span className="recent-subtitle">{cupl.tramite_name}</span>
                        </div>
                        <div className={`recent-status ${cupl.is_paid ? 'paid' : 'pending'}`}>
                          {cupl.is_paid ? 'Pagado' : 'Pendiente'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">No hay actividad reciente</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DashboardTramitesModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        filterType={modalFilterType}
        filterTitle={modalFilterTitle}
        onUpdate={fetchDashboardData}
      />

      <DashboardCuplsModal 
        isOpen={cuplModalOpen}
        onClose={() => setCuplModalOpen(false)}
        filterType={cuplModalFilterType}
        filterTitle={cuplModalFilterTitle}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

export default Dashboard;
